/**
 * The Journal home read model (F-JR-11, F-JR-12, F-JR-43).
 *
 * **One rule governs this whole file: nothing above it ever sees a durable
 * entry that a same-id draft has already superseded.** `@ff/domain` states the
 * per-record form of that rule — `journalProjection(entry, draft)` — but the
 * *library* form is what every consumer actually asks for, and composing it at
 * each call site is how one of them ends up zipping entries and drafts in the
 * wrong direction. So it is composed once, here, and the timeline, the search
 * index, the favourites lens and every reader are all built from the result.
 *
 * A pure module on purpose. The whole of F-JR-11's sort, F-JR-08's membership,
 * F-JR-10's same-day comparison and F-JR-12's precedence are decisions with no
 * DOM in them, and a rule expressed inside a component is a rule that can only
 * be checked by rendering one.
 */

import type {
  JournalDraft,
  JournalEntry,
  JournalEntryId,
  JournalEntryProjection,
  JournalGoal,
  JournalGoalId,
  JournalLens,
} from "@ff/domain";
import {
  compareJournalProjections,
  journalEntryPreview,
  journalEntryTitle,
  journalInstantToMilliseconds,
  journalLensMatches,
  journalProjection,
  journalSearchIndex,
  journalSearchMatches,
} from "@ff/domain";

/**
 * Every durable entry, each one replaced by its same-id draft's entry.
 *
 * Drafts with no persisted counterpart are deliberately **not** here: F-JR-43
 * excludes them from the timeline, and they are reached only through Continue
 * writing (F-JR-05). Including them would turn an unfinished thought into a
 * journal entry the user never chose to keep.
 */
export function journalProjections(
  entries: readonly JournalEntry[],
  drafts: readonly JournalDraft[],
): readonly JournalEntryProjection[] {
  const draftsById = new Map<JournalEntryId, JournalDraft>();
  for (const draft of drafts) {
    const existing = draftsById.get(draft.id);
    // Two drafts for one id is not a state the repository produces, but a
    // library read is a merge of files on disk. Newest `savedAt` wins, ties by
    // id, which is the same ordering `journalStandaloneDrafts` uses — a second,
    // different tiebreak here would let the timeline and Continue writing
    // disagree about which draft is "the" draft.
    if (
      existing === undefined ||
      draft.savedAt > existing.savedAt ||
      (draft.savedAt === existing.savedAt && draft.id < existing.id)
    ) {
      draftsById.set(draft.id, draft);
    }
  }
  return entries.map((entry) => journalProjection(entry, draftsById.get(entry.id)));
}

/** A local calendar day, as the three numbers a same-day test compares. */
export interface JournalCalendarDay {
  readonly year: number;
  /** 1-based, so the value reads the way a date does. */
  readonly month: number;
  readonly day: number;
}

/**
 * F-JR-10 compares "same day on `occurredAt` in the autoupdating calendar", so
 * the comparison is on the reader's *local* civil date and never on a UTC
 * instant — an entry written at 23:30 local is on the day the user says it is.
 */
export function journalCalendarDay(epochMilliseconds: number): JournalCalendarDay {
  const date = new Date(epochMilliseconds);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

export function journalSameCalendarDay(
  left: JournalCalendarDay,
  right: JournalCalendarDay,
): boolean {
  return left.year === right.year && left.month === right.month && left.day === right.day;
}

/**
 * One row of the timeline, with its two fallback chains already resolved.
 *
 * The chains are resolved here rather than in the row component because the
 * preview's last-but-one link is *the live goal's* `why` — a value the domain
 * cannot look up and a component should not be given a whole goal library to
 * find. Resolving it once, against the map the surface already holds, keeps the
 * row a renderer.
 */
export interface JournalTimelineRow {
  readonly projection: JournalEntryProjection;
  readonly entry: JournalEntry;
  readonly isDraft: boolean;
  readonly title: string;
  readonly preview: string;
  /** The working (draft-overlaid) date. F-JR-11 sorts on exactly this. */
  readonly occurredAtMilliseconds: number;
}

export interface JournalTimelineInput {
  readonly projections: readonly JournalEntryProjection[];
  readonly lens: JournalLens;
  /** Trimmed by the matcher; an empty query matches everything (F-JR-09). */
  readonly searchQuery: string;
  /** `null` when no calendar filter is applied (F-JR-10). */
  readonly day: JournalCalendarDay | null;
  /** Live goals by id, for the preview chain's `why` link (F-JR-13). */
  readonly goals: ReadonlyMap<JournalGoalId, JournalGoal>;
}

/**
 * The timeline, filtered and ordered.
 *
 * Order first, filter second. F-JR-09 says results preserve index order, and
 * filtering a list that is already in timeline order is the only way that stays
 * true without a second sort that could disagree with the first.
 */
export function journalTimeline(input: JournalTimelineInput): readonly JournalTimelineRow[] {
  const ordered = [...input.projections].sort(compareJournalProjections);

  return ordered
    .filter((projection) => journalLensMatches(input.lens, projection.entry))
    .filter((projection) => {
      if (input.day === null) return true;
      const day = journalCalendarDay(
        journalInstantToMilliseconds(projection.entry.occurredAt),
      );
      return journalSameCalendarDay(day, input.day);
    })
    .filter((projection) =>
      journalSearchMatches(input.searchQuery, journalSearchIndex(projection)),
    )
    .map((projection) => journalTimelineRow(projection, input.goals));
}

export function journalTimelineRow(
  projection: JournalEntryProjection,
  goals: ReadonlyMap<JournalGoalId, JournalGoal>,
): JournalTimelineRow {
  const entry = projection.entry;
  const goalId = entry.goal?.goalID;
  const liveGoalWhy = goalId === undefined ? undefined : goals.get(goalId)?.why;
  return {
    projection,
    entry,
    isDraft: projection.isDraft,
    title: journalEntryTitle(entry),
    // `journalEntryPreview` refuses an explicit `undefined` third link only by
    // omitting the argument, so the call is split rather than passing
    // `liveGoalWhy` straight through as a possibly-undefined value.
    preview:
      liveGoalWhy === undefined
        ? journalEntryPreview(entry)
        : journalEntryPreview(entry, liveGoalWhy),
    occurredAtMilliseconds: journalInstantToMilliseconds(entry.occurredAt),
  };
}

/**
 * The projection for one entry id, or `null`.
 *
 * The reader uses this rather than reaching into the repository, so a reader
 * opened while a draft exists shows the draft's text — the same guarantee the
 * timeline gives, from the same source (F-JR-43).
 */
export function journalProjectionById(
  projections: readonly JournalEntryProjection[],
  id: JournalEntryId,
): JournalEntryProjection | null {
  return projections.find((projection) => projection.entry.id === id) ?? null;
}
